import { useState, useRef, useEffect, useCallback } from "react";
import { Icon } from "./ui";
import { IMAGE_MAX_DIMENSION, IMAGE_QUALITY } from "../utils/image";

interface CameraProps {
  onCapture: (file: File) => void;
  onClose: () => void;
}

interface CameraDevice {
  deviceId: string;
  label: string;
}

const CAMERA_STORAGE_KEY = "finchpay-preferred-camera";

export function Camera({ onCapture, onClose }: CameraProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [cameras, setCameras] = useState<CameraDevice[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stop all tracks in the current stream
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  // Get list of available cameras (preferring back cameras)
  const getCameras = useCallback(async () => {
    try {
      // First request permission with environment facing mode
      const tempStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      tempStream.getTracks().forEach((track) => track.stop());

      // Now enumerate devices
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((d) => d.kind === "videoinput");

      // Filter for back cameras
      let backCameras = videoDevices.filter((d) => {
        const label = d.label.toLowerCase();
        return (
          label.includes("back") ||
          label.includes("rear") ||
          label.includes("environment") ||
          label.includes("camera2") ||
          label.includes("camera 2") ||
          label.includes("camera0") ||
          label.includes("camera 0")
        );
      });

      // If no back cameras found by label, use all cameras
      if (backCameras.length === 0) {
        backCameras = videoDevices;
      }

      const cameraList: CameraDevice[] = backCameras.map((cam, idx) => ({
        deviceId: cam.deviceId,
        label: cam.label || `Camera ${idx + 1}`,
      }));

      setCameras(cameraList);
      return cameraList;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to access camera";
      setError(message);
      return [];
    }
  }, []);

  // Start camera with specific device ID or default
  const startCamera = useCallback(
    async (deviceId?: string) => {
      stopStream();
      setIsReady(false);
      setError(null);

      try {
        // Request high resolution - we'll scale down when capturing
        const videoConstraints: MediaTrackConstraints = {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          ...(deviceId
            ? { deviceId: { exact: deviceId } }
            : { facingMode: "environment" }),
        };
        const constraints: MediaStreamConstraints = {
          video: videoConstraints,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          // Wait for video to be ready
          videoRef.current.onloadedmetadata = () => {
            setIsReady(true);
          };
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to start camera";
        setError(message);
      }
    },
    [stopStream]
  );

  // Initialize camera on mount
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      const cameraList = await getCameras();
      if (mounted && cameraList.length > 0) {
        // Check for saved camera preference
        const savedCameraId = localStorage.getItem(CAMERA_STORAGE_KEY);
        const savedCameraIndex = savedCameraId
          ? cameraList.findIndex((c) => c.deviceId === savedCameraId)
          : -1;

        // Use saved camera if found, otherwise use first camera
        const initialIndex = savedCameraIndex >= 0 ? savedCameraIndex : 0;
        setCurrentCameraIndex(initialIndex);
        await startCamera(cameraList[initialIndex].deviceId);
      }
    };

    init();

    return () => {
      mounted = false;
      stopStream();
    };
  }, [getCameras, startCamera, stopStream]);

  // Switch to next camera
  const handleSwitchCamera = async () => {
    if (cameras.length <= 1) return;

    const nextIndex = (currentCameraIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];
    setCurrentCameraIndex(nextIndex);

    // Save preference to localStorage
    localStorage.setItem(CAMERA_STORAGE_KEY, nextCamera.deviceId);

    await startCamera(nextCamera.deviceId);
  };

  // Capture photo from video stream
  const handleCapture = () => {
    if (!videoRef.current || !canvasRef.current || !isReady) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    // Scale to shared compression settings
    const MAX_DIMENSION = IMAGE_MAX_DIMENSION;
    let width = video.videoWidth;
    let height = video.videoHeight;

    if (width > height) {
      if (width > MAX_DIMENSION) {
        height = Math.round(height * (MAX_DIMENSION / width));
        width = MAX_DIMENSION;
      }
    } else {
      if (height > MAX_DIMENSION) {
        width = Math.round(width * (MAX_DIMENSION / height));
        height = MAX_DIMENSION;
      }
    }

    // Set canvas size to scaled dimensions
    canvas.width = width;
    canvas.height = height;

    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, width, height);

    // Convert to blob and then to File
    canvas.toBlob(
      (blob) => {
        if (blob) {
          // Log size for debugging/verification
          console.log(`Captured image size: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
          
          const file = new File([blob], `receipt-${Date.now()}.jpg`, {
            type: "image/jpeg",
          });
          stopStream();
          onCapture(file);
        }
      },
      "image/jpeg",
      IMAGE_QUALITY
    );
  };

  // Handle close
  const handleClose = () => {
    stopStream();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Camera view */}
      <div className="flex-1 relative overflow-hidden">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Loading state */}
        {!isReady && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-white text-center">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm">Starting camera...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black px-6">
            <div className="text-white text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <span className="text-red-500 text-3xl">!</span>
              </div>
              <p className="text-lg font-medium mb-2">Camera Error</p>
              <p className="text-sm text-gray-400 mb-6">{error}</p>
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-white text-black rounded-xl font-medium"
              >
                Go Back
              </button>
            </div>
          </div>
        )}

        {/* Top controls */}
        <div className="absolute top-0 left-0 right-0 p-4">
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center"
          >
            <Icon name="arrow-left" size={24} className="text-white" />
          </button>
        </div>

        {/* Viewfinder frame */}
        {isReady && !error && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-8 border-2 border-white/30 rounded-2xl" />
            {/* Corner accents */}
            <div className="absolute top-8 left-8 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-xl" />
            <div className="absolute top-8 right-8 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-xl" />
            <div className="absolute bottom-8 left-8 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-xl" />
            <div className="absolute bottom-8 right-8 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-xl" />
          </div>
        )}
      </div>

      {/* Bottom controls */}
      <div className="bg-black px-6 py-8 safe-area-bottom">
        <div className="flex items-center justify-center">
          {/* Left spacer */}
          <div className="flex-1 flex justify-center">
            {/* Empty for balance */}
          </div>

          {/* Capture button */}
          <button
            onClick={handleCapture}
            disabled={!isReady || !!error}
            className="w-20 h-20 rounded-full bg-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className="w-16 h-16 rounded-full border-4 border-black" />
          </button>

          {/* Camera switcher */}
          <div className="flex-1 flex justify-center">
            {cameras.length > 1 && (
              <button
                onClick={handleSwitchCamera}
                className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center active:bg-white/30"
              >
                <Icon name="switch-camera" size={24} className="text-white" />
              </button>
            )}
          </div>
        </div>

        {/* Hint text */}
        <p className="text-center text-white/60 text-sm mt-4">
          Position receipt within the frame
        </p>
      </div>
    </div>
  );
}
